import React, { useEffect, useState } from 'react';
import {
  CompanyUserCall,
  CompanyUserCallsPage,
  getCompanyUserCalls,
} from '../companyHelpers';
import style from './CompanyCallsTab.module.css';

/* §FB41: вкладка «Дзвінки» сторінки компанії — журнал УСІХ дзвінків користувачів
   компанії: співробітник → перекладач (вихідний) і перекладач → співробітник
   (вхідний). Дані пагіновані; «Показати ще» довантажує наступну сторінку. */

const PAGE_SIZE = 30;

// Дата/час дзвінка в київському поясі: «08.07.2026, 19:14».
const formatCallAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

const formatDuration = (secs?: number | null): string => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const sRest = secs % 60;
  return m > 0 ? `${m} хв ${String(sRest).padStart(2, '0')} с` : `${sRest} с`;
};

// Бейдж результату (outcome рахує бекенд).
const OUTCOME_VIEW: Record<string, { label: string; tone: 'ok' | 'live' | 'bad' }> = {
  finished: { label: 'Відбувся', tone: 'ok' },
  active: { label: 'Триває', tone: 'live' },
  ringing: { label: 'Дзвонить…', tone: 'live' },
  canceled: { label: 'Відхилено', tone: 'bad' },
  missed: { label: 'Без відповіді', tone: 'bad' },
};

const DIRECTION_VIEW: Record<string, string> = {
  outgoing: '↗ Вихідний',
  incoming: '↙ Вхідний',
  internal: '⇄ Внутрішній',
};

interface CompanyCallsTabProps {
  companyId: number;
}

const CompanyCallsTab: React.FC<CompanyCallsTabProps> = ({ companyId }) => {
  const [pageData, setPageData] = useState<CompanyUserCallsPage | null>(null);
  const [items, setItems] = useState<CompanyUserCall[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadPage = async (pageIndex: number, replace: boolean) => {
    setLoading(true);
    setError(false);
    try {
      const data = await getCompanyUserCalls(companyId, pageIndex, PAGE_SIZE);
      setPageData(data);
      // При довантаженні прибираємо можливі дублі (список міг зсунутись новими дзвінками).
      setItems((prev) =>
        replace
          ? data.items
          : [
              ...prev,
              ...data.items.filter((n) => !prev.some((p) => p.id === n.id)),
            ],
      );
      setPage(pageIndex);
    } catch {
      setError(true);
      if (replace) setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className={style.wrap}>
      <div className={style.card}>
        <div className={style.head}>
          <h5 className={style.title}>Дзвінки користувачів компанії</h5>
          {pageData && pageData.totalCount > 0 && (
            <span className={style.count}>{pageData.totalCount}</span>
          )}
          <button
            type="button"
            className={style.refresh}
            onClick={() => loadPage(0, true)}
            disabled={loading}
            title="Оновити журнал"
            aria-label="Оновити журнал"
          >
            ⟳
          </button>
        </div>
        <p className={style.subhint}>
          Вихідні — співробітник викликав перекладача; вхідні — дзвонили
          співробітнику компанії.
        </p>

        {loading && items.length === 0 && (
          <p className={style.muted}>Завантаження…</p>
        )}
        {error && items.length === 0 && !loading && (
          <p className={style.errorText}>
            Не вдалося завантажити журнал. Спробуйте ще раз.
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className={style.muted}>
            Дзвінків користувачів цієї компанії ще не було.
          </p>
        )}

        {items.length > 0 && (
          <div className={style.list}>
            <div className={`${style.row} ${style.headRow}`}>
              <span>Дата і час</span>
              <span>Співробітник</span>
              <span>Напрям</span>
              <span>Співрозмовник</span>
              <span>Тривалість</span>
              <span>Статус</span>
              <span>Коментар</span>
            </div>
            {items.map((c) => {
              const view = OUTCOME_VIEW[c.outcome] ?? OUTCOME_VIEW.missed;
              const toneClass =
                view.tone === 'ok'
                  ? style.tOk
                  : view.tone === 'live'
                    ? style.tLive
                    : style.tBad;
              return (
                <div key={c.id} className={style.row}>
                  <span className={style.cDate}>
                    {formatCallAt(c.createdAt)}
                  </span>
                  <span className={style.cName} title={c.employeeName}>
                    {c.employeeName || '—'}
                  </span>
                  <span
                    className={
                      c.direction === 'incoming'
                        ? `${style.cDir} ${style.cDirIn}`
                        : style.cDir
                    }
                  >
                    {DIRECTION_VIEW[c.direction] ?? c.direction}
                  </span>
                  <span className={style.cName} title={c.counterpartName}>
                    {c.counterpartName || '—'}
                  </span>
                  <span className={style.cDur}>
                    {formatDuration(c.durationSeconds)}
                  </span>
                  <span className={style.cStatus}>
                    <span className={`${style.badge} ${toneClass}`}>
                      {view.label}
                    </span>
                    {typeof c.rating === 'number' && c.rating > 0 && (
                      <span
                        className={style.rating}
                        title="Оцінка дзвінка користувачем"
                      >
                        ★ {c.rating}
                      </span>
                    )}
                  </span>
                  <span
                    className={style.cComment}
                    title={c.ratingComment || undefined}
                  >
                    {c.ratingComment || '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {pageData?.hasNextPage && items.length > 0 && (
          <button
            type="button"
            className={style.moreBtn}
            onClick={() => loadPage(page + 1, false)}
            disabled={loading}
          >
            {loading ? 'Завантаження…' : 'Показати ще'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyCallsTab;
